import { useCallback, useEffect, useRef, useState } from 'react';

var POLL_INTERVAL_MS = 60000;
var MAX_CONSECUTIVE_FAILURES = 2;

// Owns fetching for the dashboard.
//
// Auto-refresh is a load multiplier on an aggregating endpoint, so it is deliberately
// conservative: it updates in place (never blanking the screen), pauses while the tab is
// hidden, and stops entirely after two consecutive failures until the user retries.
export default function useMonitoringData(date, hospitalId) {
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState(null);
  var [updatedAt, setUpdatedAt] = useState(null);
  var [refreshPaused, setRefreshPaused] = useState(false);

  var failures = useRef(0);

  var load = useCallback(function (options) {
    var background = options && options.background;
    if (!background) setLoading(true);

    var params = new URLSearchParams({ date: date });
    if (hospitalId) params.set('hospital_id', hospitalId);

    return fetch('/api/ehc/monitoring/daily?' + params.toString(), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).
      then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      }).
      then(function (json) {
        failures.current = 0;
        setRefreshPaused(false);
        setData(json);
        setError(null);
        setUpdatedAt(new Date());
      }).
      catch(function (err) {
        failures.current += 1;
        if (failures.current >= MAX_CONSECUTIVE_FAILURES) setRefreshPaused(true);
        setError(err.message);
      }).
      then(function () {
        if (!background) setLoading(false);
      });
  }, [date, hospitalId]);

  useEffect(function () {
    failures.current = 0;
    setRefreshPaused(false);
    load();
  }, [load]);

  useEffect(function () {
    if (refreshPaused) return undefined;

    var timer = setInterval(function () {
      if (document.hidden) return;
      load({ background: true });
    }, POLL_INTERVAL_MS);

    return function () { clearInterval(timer); };
  }, [load, refreshPaused]);

  var retry = useCallback(function () {
    failures.current = 0;
    setRefreshPaused(false);
    return load();
  }, [load]);

  return {
    data: data,
    loading: loading,
    error: error,
    updatedAt: updatedAt,
    refreshPaused: refreshPaused,
    maxFailures: MAX_CONSECUTIVE_FAILURES,
    reload: load,
    retry: retry
  };
}
